import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import "../css/EditEmployeeform.css";
import { BASE_URL } from "../config";
import UserFallback from "../images/logo1.jpg";
import CustomButton from "./CustomButton";
import { getAuthHeaders } from "../services/useAuth";
import formatDate from "../utils/formatDate";
import { toast } from "react-toastify";
import Loader from "../dist/Loader";

const normalizeYMD = (val) => {
  if (!val) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  const d = new Date(val);
  if (isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const getTodayYMD = () => new Date().toISOString().split("T")[0];

const getEighteenYearsAgo = () => {
  const t = new Date();
  t.setFullYear(t.getFullYear() - 18);
  return t.toISOString().split("T")[0];
};

// Centralized authenticated API call
const authApiCall = async (
  method,
  url,
  { data = null, params = null } = {},
) => {
  try {
    const res = await axios({
      method,
      url,
      headers: getAuthHeaders(),
      data,
      params,
    });
    return res.data;
  } catch (error) {
    if (error.response?.status === 403) {
      localStorage.clear();
      window.location.href = "/login";
    }
    throw error;
  }
};

const EditEmployeeForm = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    username: "",
    firstName: "",
    lastName: "",
    fatherName: "",
    dob: "",
    gender: "Male",

    email: "",
    mobile: "",

    department: "SALES",
    division: "",
    designation: "",
    manager: "",

    address: "",
    pin: "",
    country: "INDIA",

    state: "",
    city: "",
    subState: "",
    headQuarter: "",

    marriageAnniversary: "",
    finalJoiningDate: "",
    trainingDate: "",
    confirmDate: "",

    resignationDate: "",
    leftDate: "",
    leftReason: "",
    nocDate: "",
    fullAndFinalDate: "",

    finalAmount: "0.00",
    status: "WORKING",
    remarks: "",

    profile_image: "",
  });

  const [errors, setErrors] = useState({
    employee: "",
    firstName: "",
    lastName: "",
    fatherName: "",
    dob: "",
    mobile: "",
    email: "",
    pin: "",
    designation: "",
    state: "",
    city: "",
    subState: "",
    finalJoiningDate: "",
  });

  const [divisions, setDivisions] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [headquarters, setHeadquarters] = useState([]);
  const [subStates, setSubStates] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [originalManager, setOriginalManager] = useState("");
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  // ─── Loading state: tracks initial employee fetch ───────────────────────────
  const [loading, setLoading] = useState(true);

  // ─── Pending API counter: tracks dependent dropdown/list fetches ─────────────
  const [pendingApiCount, setPendingApiCount] = useState(0);

  // ─── Submission in progress ──────────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ─── Static lists loaded flag: blocks Update until employees/divisions/states/designations are ready ──
  const [staticDataReady, setStaticDataReady] = useState(false);

  const startLoading = () => setPendingApiCount((c) => c + 1);
  const stopLoading = () => setPendingApiCount((c) => Math.max(0, c - 1));

  // True whenever ANY async operation is in flight
  const isDataLoading = loading || pendingApiCount > 0 || !staticDataReady;

  // True when form should be blocked from submission
  const isFormBusy = isDataLoading || isSubmitting;

  const [existingMobiles, setExistingMobiles] = useState([]);
  const [isMarriageChecked, setIsMarriageChecked] = useState(false);
  const [originalMobile, setOriginalMobile] = useState("");
  const [managers, setManagers] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(false);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState("");

  // ─── Persist profile image from localStorage on mount ───────────────────────
  useEffect(() => {
    const savedImage = localStorage.getItem(`profile_image_${employeeId}`);
    if (savedImage) {
      setFormData((prev) => ({ ...prev, profile_image: savedImage }));
    }
  }, [employeeId]);

  // ─── Field-level validation ──────────────────────────────────────────────────
  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case "firstName":
        if (name === "firstName" && !value) {
          error = "First name is required";
        } else if (value && !/^[A-Za-z\s]+$/.test(value)) {
          error = "Only alphabets allowed";
        }
        break;
      case "fatherName":
        if (!value) error = "Father's name is required";
        else if (!/^[A-Za-z\s]+$/.test(value)) error = "Only alphabets allowed";
        break;
      case "dob": {
        if (!value) {
          error = "Date of birth is required";
        } else if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
          error = "DOB must be in YYYY-MM-DD";
        } else {
          const dob = new Date(value);
          const today = new Date();
          let age = today.getFullYear() - dob.getFullYear();
          const m = today.getMonth() - dob.getMonth();
          if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
          if (age < 18) error = "Employee must be at least 18 years old";
        }
        break;
      }
      case "mobile":
        if (value === originalMobile) {
          error = "";
        } else if (!value) {
          error = "Mobile number is required";
        } else if (!/^\d{10}$/.test(value)) {
          error = "Mobile number must be 10 digits";
        } else if (existingMobiles.includes(value)) {
          error = "Mobile number already exists";
        }
        break;
      case "email":
        if (!value) error = "Email is required";
        else if (
          !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(value)
        ) {
          error = "Invalid email format";
        }
        break;
      case "pin":
        if (!value) error = "PIN code is required";
        else if (!/^\d{6}$/.test(value))
          error = "PIN code must be exactly 6 digits";
        break;
      default:
        break;
    }
    return error;
  };

  // ─── Image selection handler ─────────────────────────────────────────────────
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        alert("Please select a valid image file");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert("Image size should be less than 5MB");
        return;
      }
      setProfileImageFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setProfileImagePreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // ─── Image upload handler ────────────────────────────────────────────────────
  const handleImageUpload = async () => {
    if (!profileImageFile || !employeeId) {
      alert("Please select an image first");
      return;
    }
    try {
      const imgForm = new FormData();
      imgForm.append("profile_image", profileImageFile);
      const res = await axios.post(
        `${BASE_URL}/api/employees/${employeeId}/profile_image`,
        imgForm,
        {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "multipart/form-data",
          },
        },
      );
      const uploadedImageUrl = res.data?.profile_image || res.data?.image || "";
      setFormData((prev) => ({ ...prev, profile_image: uploadedImageUrl }));
      setProfileImagePreview("");
      setProfileImageFile(null);
      alert("Profile image uploaded successfully");
    } catch (error) {
      console.error("Image upload failed:", error);
      alert("Image upload failed");
    }
  };

  const handleMarriageCheckbox = () => {
    setIsMarriageChecked((prev) => {
      const next = !prev;
      if (!next) {
        setFormData((f) => ({ ...f, marriageAnniversary: "" }));
      }
      return next;
    });
  };

  // ─── Fetch employee data ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!employeeId) return;
    (async () => {
      setLoading(true);
      try {
        const employee = await authApiCall(
          "get",
          `${BASE_URL}/api/employees/${employeeId}`,
        );

        const dob = employee.dob;
        const marriage_anniversary = employee.marriage_anniversary;
        const training_date = employee.training_date;
        const confirm_date = employee.confirm_date;
        const final_joining_date = employee.final_joining_date;
        const resign_date = employee.resign_date;
        const left_date = employee.left_date;
        const noc_date = employee.noc_date;
        const full_and_final_date = employee.full_and_final_date;

        setFormData((prev) => ({
          ...prev,
          username: employee.employee,
          firstName: employee.first_name || "",
          lastName: employee.last_name || "",
          fatherName: employee.father_name || "",
          division: employee.division || "",
          manager: employee.manager || "",
          managerName: employee.manager_name || "",
          dob,
          marriageAnniversary: marriage_anniversary || "",
          gender: employee.gender || "Male",
          department: employee.department || "SALES",
          country: employee.country || "INDIA",
          city: employee.city_id ? String(employee.city_id) : "",
          mobile: employee.mobile || "",
          headQuarter: employee.headquarter_id || "",
          trainingDate: training_date || "",
          confirmDate: confirm_date || "",
          finalJoiningDate: final_joining_date || "",
          designation: employee.designation || "",
          state: employee.state_id ? String(employee.state_id) : "",
          subState: employee.substate_id || "",
          address: employee.address || "",
          pin: employee.pin || "",
          email: employee.email || "",
          remarks: employee.remarks || "",
          status:
            employee.status?.trim().toUpperCase() === "RESIGNED"
              ? "RESIGNED"
              : "WORKING",
          resignationDate: resign_date || "",
          leftDate: left_date || "",
          leftReason: employee.left_reason || "",
          nocDate: noc_date || "",
          finalAmount: employee.final_amount
            ? String(employee.final_amount)
            : "0.00",
          fullAndFinalDate: full_and_final_date || "",
          profile_image: employee.profile_image || "",
        }));

        setOriginalMobile(employee.mobile || "");
        setOriginalManager(employee.manager || "");
        setIsMarriageChecked(!!employee.marriage_anniversary);

        // Fetch dependent dropdowns BEFORE releasing the loading lock
        if (employee.state_id) {
          await Promise.all([
            fetchCities(employee.state_id),
            fetchSubStates(employee.state_id),
          ]);
        }
        if (employee.city_id) {
          await fetchHeadquarters(employee.city_id);
        }
      } catch (e) {
        console.error("Error fetching employee:", e);
      } finally {
        // Only release lock after employee + all dependent data is ready
        setLoading(false);
      }
    })();
  }, [employeeId]);

  // ─── Fetch static lists (employees, divisions, states, designations) ─────────
  useEffect(() => {
    (async () => {
      setStaticDataReady(false);
      try {
        const [employeesRes, divisionsRes, statesRes, designationsRes] =
          await Promise.all([
            authApiCall("get", `${BASE_URL}/api/employees`),
            authApiCall("get", `${BASE_URL}/api/divisions`),
            authApiCall("get", `${BASE_URL}/api/states`),
            authApiCall("get", `${BASE_URL}/api/designations/`),
          ]);

        const mobiles = employeesRes
          .map((emp) => emp.mobile)
          .filter((m) => !!m);
        setExistingMobiles(mobiles);
        setDivisions(divisionsRes || []);
        setStates(statesRes || []);
        setDesignations(designationsRes || []);
      } catch (e) {
        console.error("Error fetching static data:", e);
      } finally {
        // Mark static data as ready regardless — partial data is better than a
        // permanently blocked form; individual fields will be empty if API failed.
        setStaticDataReady(true);
      }
    })();
  }, []);

  // ─── Fetch history ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!employeeId) return;
    (async () => {
      try {
        const historyData = await authApiCall(
          "get",
          `${BASE_URL}/api/employee/history/${employeeId}`,
        );
        setHistory((historyData || []).slice().reverse());
      } catch (e) {
        console.error("Error fetching history:", e);
      }
    })();
  }, [refreshTrigger, employeeId]);

  // ─── Fetch managers when designation or state changes ───────────────────────
  useEffect(() => {
    (async () => {
      if (!formData.designation || !formData.state) {
        setManagers([]);
        return;
      }
      startLoading();
      try {
        const data = await authApiCall(
          "get",
          `${BASE_URL}/api/managers-by-designation`,
          {
            params: {
              designation: formData.designation,
              state_id: formData.state,
            },
          },
        );

        let managersData = [];
        if (Array.isArray(data)) managersData = data;
        else if (data?.data) managersData = data.data;
        else if (data?.managers) managersData = data.managers;
        else if (data?.results) managersData = data.results;

        setManagers(managersData || []);

        const hasCurrent = (managersData || []).some(
          (m) => m.username === formData.manager
        );

        // Existing manager ko preserve rakho
        if (!hasCurrent) {
          console.log(
            "Manager not found in dropdown, preserving existing manager:",
            formData.manager
          );
        }
      } catch (e) {
        console.error("Error fetching managers:", e);
        setManagers([]);
      } finally {
        stopLoading();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.designation, formData.state]);

  // ─── Dependent dropdown fetchers ─────────────────────────────────────────────
  const fetchCities = async (stateId) => {
    startLoading();
    try {
      const citiesData = await authApiCall(
        "get",
        `${BASE_URL}/api/cities/${stateId}`,
      );
      setCities(citiesData || []);
    } catch (e) {
      console.error("Error fetching cities:", e);
    } finally {
      stopLoading();
    }
  };

  const fetchHeadquarters = async (cityId) => {
    startLoading();
    try {
      const hqData = await authApiCall(
        "get",
        `${BASE_URL}/api/headquarters/${cityId}`,
      );
      setHeadquarters(hqData || []);
      if (Array.isArray(hqData) && hqData.length === 1) {
        setFormData((prev) => ({ ...prev, headQuarter: hqData[0].id }));
      }
    } catch (e) {
      console.error("Error fetching headquarters:", e);
    } finally {
      stopLoading();
    }
  };

  const fetchSubStates = async (stateId) => {
    if (!stateId) {
      setSubStates([]);
      return;
    }
    startLoading();
    try {
      const subStatesData = await authApiCall(
        "get",
        `${BASE_URL}/api/substates/${stateId}`,
      );
      setSubStates(subStatesData || []);
      if (Array.isArray(subStatesData) && subStatesData.length === 1) {
        setFormData((prev) => ({ ...prev, subState: subStatesData[0].id }));
      }
    } catch (e) {
      console.error("Error fetching substates:", e);
    } finally {
      stopLoading();
    }
  };

  // ─── Change handler ──────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;

    const normalized =
      name === "dob" ||
        name === "marriageAnniversary" ||
        name === "trainingDate" ||
        name === "confirmDate" ||
        name === "finalJoiningDate" ||
        name === "resignationDate" ||
        name === "joiningDate" ||
        name === "leftDate" ||
        name === "nocDate" ||
        name === "fullAndFinalDate"
        ? normalizeYMD(value)
        : value;

    const error = validateField(name, normalized);
    setErrors((prev) => ({ ...prev, [name]: error }));
    setFormData((prev) => ({ ...prev, [name]: normalized }));

    if (name === "state") {
      setFormData((prev) => ({
        ...prev,
        [name]: normalized,
        city: "",
        headQuarter: "",
        subState: "",
      }));
      setHeadquarters([]);
      if (normalized) {
        fetchCities(normalized);
        fetchSubStates(normalized);
      } else {
        setCities([]);
        setSubStates([]);
      }
    }

    if (name === "city") {
      setFormData((prev) => ({ ...prev, [name]: normalized, headQuarter: "" }));
      setHeadquarters([]);
      if (normalized) fetchHeadquarters(normalized);
    }

    if (name === "headQuarter") {
      setFormData((prev) => ({ ...prev, [name]: normalized, subState: "" }));
    }
  };

  // ─── Resignation date guard ──────────────────────────────────────────────────
  useEffect(() => {
    if (formData.resignationDate && formData.joiningDate) {
      const j = new Date(formData.joiningDate);
      const r = new Date(formData.resignationDate);
      if (r < j) {
        alert("Resignation date should not be before joining date.");
        setFormData((prev) => ({ ...prev, resignationDate: "" }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.resignationDate, formData.joiningDate]);

  // ─── Form-level validation ───────────────────────────────────────────────────
  const validateForm = () => {
    const newErrors = {};

    if (!formData.username?.trim()) newErrors.username = "User ID required";
    if (!formData.firstName?.trim()) newErrors.firstName = "First name required";
    if (!formData.gender) newErrors.gender = "Gender required";
    if (!formData.mobile || formData.mobile.length !== 10)
      newErrors.mobile = "Valid mobile required";
    if (!formData.department) newErrors.department = "Department required";
    if (!formData.designation) newErrors.designation = "Designation required";
    if (!formData.status) newErrors.status = "Status required";
    if (!formData.state) newErrors.state = "State required";
    if (!formData.city) newErrors.city = "City required";
    if (!(formData.manager || originalManager)) {
      newErrors.manager = "Manager is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ─── Submit handler ──────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Hard guard: do not allow submission while any API is still in flight
    if (isFormBusy) {
      toast.warn("Please wait — data is still loading.");
      return;
    }

    if (!validateForm()) {
      toast.error("Please fill all required fields");
      return;
    }

    let resignationDate = formData.resignationDate;
    let leftDate = formData.leftDate;
    let trainingDate = formData.trainingDate;
    let confirmDate = formData.confirmDate;
    let nocDate = formData.nocDate;
    let fullAndFinalDate = formData.fullAndFinalDate;
    let leftReason = formData.leftReason;
    let finalAmount = formData.finalAmount;

    if (formData.status === "WORKING") {
      resignationDate = null;
      leftDate = null;
      trainingDate = null;
      confirmDate = null;
      nocDate = null;
      fullAndFinalDate = null;
      leftReason = null;
      finalAmount = 0;
    }

    if (formData.status === "RESIGNED") {
      if (!resignationDate) {
        resignationDate = getTodayYMD();
      }
    }

    const updatedData = {
      employee: formData.username,
      first_name: formData.firstName,
      last_name: formData.lastName,
      father_name: formData.fatherName,

      dob: formData.dob || null,
      gender: formData.gender,

      email: formData.email,
      mobile: formData.mobile,

      manager: formData.manager || originalManager,
      department: formData.department,
      division: formData.division,
      designation: formData.designation,

      address: formData.address,
      pin: formData.pin,
      country: formData.country,

      marriage_anniversary: formData.marriageAnniversary || null,
      final_joining_date: formData.finalJoiningDate || null,

      resign_date: resignationDate || null,
      left_date: leftDate || null,
      left_reason: leftReason || null,
      training_date: trainingDate || null,
      confirm_date: confirmDate || null,
      noc_date: nocDate || null,
      full_and_final_date: fullAndFinalDate || null,
      final_amount: finalAmount ? parseFloat(finalAmount) : 0,

      status: formData.status,
      remarks: formData.remarks,

      state_id: formData.state || null,
      city_id: formData.city ? parseInt(formData.city, 10) : null,
      headquarter_id: formData.headQuarter || null,
      substate_id: formData.subState || null,
    };

    setIsSubmitting(true);
    try {
      await authApiCall("put", `${BASE_URL}/api/employees/${employeeId}`, {
        data: updatedData,
      });

      toast.success("Employee updated successfully!");
      setRefreshTrigger((p) => !p);
    } catch (e) {
      console.error("Error updating employee:", e);

      const apiErrors = e?.response?.data?.detail;

      if (Array.isArray(apiErrors)) {
        const newErrors = {};

        apiErrors.forEach((err) => {
          const field = err.loc?.[1]; // e.g. "email"
          const message = err.msg;

          if (field) {
            newErrors[field] = message;
          }
        });

        // ✅ Set field-level errors
        setErrors((prev) => ({ ...prev, ...newErrors }));

        // ✅ Show first error in toast
        toast.error(apiErrors[0]?.msg || "Validation error");
      } else {
        // fallback error
        toast.error(
          e?.response?.data?.message || "Something went wrong"
        );
      }
    }
    finally {
      setIsSubmitting(false);
    }
  };

  // ─── Full-page loader while initial employee data loads ──────────────────────
  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/25">
        <Loader onCancel={() => setIsLoading(false)} />
      </div>
    );
  }

  // ─── Button label helper ─────────────────────────────────────────────────────
  const getButtonLabel = () => {
    if (isSubmitting) return "Saving...";
    if (pendingApiCount > 0) return "Loading...";
    if (!staticDataReady) return "Loading...";
    return "Update";
  };

  return (
    <div className="editemployee-main-container">
      <div className="p-2 border-2 border-gray-300 rounded-lg">
        <div className="flex items-center justify-center relative mb-4">
          <h1 className="text-xl font-bold text-center text-gray-800">
            <span className="text-blue-700 whitespace-nowrap">Edit </span>
            <span className="text-blue-500">Employee</span>
          </h1>

          <div className="absolute right-0 flex flex-col items-center justify-center mr-4 mt-8">
            <img
              src={
                profileImagePreview || formData.profile_image || UserFallback
              }
              alt="Employee Profile Image"
              onError={(e) => {
                console.error("Image load failed:", e?.target?.src);
                e.target.onerror = null;
                e.target.src = UserFallback;
              }}
              className="w-9 h-8 rounded-full object-cover border-2 border-blue-500 shadow-sm mb-2"
            />
            <div className="flex flex-col items-center gap-1 mb-3">
              <label className="cursor-pointer text-xs text-blue-600 hover:text-blue-800 font-medium">
                Choose Image
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
              {profileImageFile && (
                <button
                  type="button"
                  onClick={handleImageUpload}
                  className="text-xs bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600"
                >
                  Save Image
                </button>
              )}
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>

          {/* ── Basic Details ─────────────────────────────────────────────── */}
          <fieldset className="editemployee-section border rounded-md p-4">
            <legend className="bg-blue-500 border rounded text-white px-3">
              Basic Details
            </legend>

            <div className="editemployee-grid-container">
              <label className="editemployee-required-label">USER ID</label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md p-1"
              />

              <label className="editemployee-required-label">FIRST NAME</label>
              <div>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-1"
                />
                {errors.firstName && (
                  <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
                )}
              </div>

              <label>LAST NAME</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md p-1"
              />

              <label className="editemployee-required-label">FATHER NAME</label>
              <div>
                <input
                  type="text"
                  name="fatherName"
                  value={formData.fatherName}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-1"
                />
                {errors.fatherName && (
                  <p className="text-red-500 text-xs mt-1">{errors.fatherName}</p>
                )}
              </div>

              <label>DIVISION</label>
              <select
                name="division"
                value={formData.division}
                onChange={handleChange}
                disabled={!staticDataReady}
                className="w-full border border-gray-300 rounded-md p-1 disabled:opacity-50"
              >
                <option value="">
                  {!staticDataReady ? "Loading…" : "Select Division"}
                </option>
                {divisions.map((div) => (
                  <option key={div.id} value={div.name}>
                    {div.name}
                  </option>
                ))}
              </select>

              <label className="editemployee-required-label">DESIGNATION</label>
              <div>
                <select
                  name="designation"
                  value={formData.designation}
                  onChange={handleChange}
                  disabled={!staticDataReady}
                  className="w-full border border-gray-300 rounded-md p-1 disabled:opacity-50"
                >
                  <option value="">
                    {!staticDataReady ? "Loading…" : "Select Designation"}
                  </option>
                  {designations.map((des) => (
                    <option key={des.id} value={des.name}>
                      {des.name}
                    </option>
                  ))}
                </select>
                {errors.designation && (
                  <p className="text-red-500 text-xs mt-1">{errors.designation}</p>
                )}
              </div>

              <label className="editemployee-required-label">STATE</label>
              <div>
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  disabled={!staticDataReady}
                  className="w-full border border-gray-300 rounded-md p-1 disabled:opacity-50"
                >
                  <option value="">
                    {!staticDataReady ? "Loading…" : "Select State"}
                  </option>
                  {states.map((state) => (
                    <option key={state.id} value={state.id}>
                      {state.name}
                    </option>
                  ))}
                </select>
                {errors.state && (
                  <p className="text-red-500 text-xs mt-1">{errors.state}</p>
                )}
              </div>

              <label>MANAGER</label>
              <select
                name="manager"
                required
                value={formData.manager}
                onChange={handleChange}
                disabled={pendingApiCount > 0}
                className="w-full border border-gray-300 rounded-md p-1 disabled:opacity-50"
              >
                {originalManager &&
                  !managers.some(
                    (m) => m.employee === originalManager
                  ) && (
                    <option value={originalManager}>
                      {formData.managerName || originalManager}
                    </option>
                  )}
                {managers.map((mgr) => (
                  <option
                key={mgr.employee_id}
                value={mgr.employee_name}
              >
                {mgr.first_name} {mgr.last_name} ({mgr.designation})
        </option>
                ))}
              </select>


              <label className="editemployee-required-label">SUB STATE</label>
              <select
                name="subState"
                value={formData.subState}
                onChange={handleChange}
                disabled={pendingApiCount > 0}
                className="w-full border border-gray-300 rounded-md p-1 disabled:opacity-50"
              >
                <option value="">
                  {pendingApiCount > 0 ? "Loading…" : "Select"}
                </option>
                {subStates.map((sub) => (
                  <option key={sub.id} value={sub.id}>
                    {sub.name}
                  </option>
                ))}
              </select>

              <label>MARRIAGE ANNIVERSARY</label>
              <input
                type="date"
                name="marriageAnniversary"
                value={formData.marriageAnniversary}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md p-1"
              />

              <label className="editemployee-required-label">CITY</label>
              <div>
                <select
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  disabled={pendingApiCount > 0}
                  className="w-full border border-gray-300 rounded-md p-1 disabled:opacity-50"
                >
                  <option value="">
                    {pendingApiCount > 0 ? "Loading…" : "Select"}
                  </option>
                  {cities.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                    </option>
                  ))}
                </select>
                {errors.city && (
                  <p className="text-red-500 text-xs mt-1">{errors.city}</p>
                )}
              </div>

              <label className="editemployee-required-label">DEPARTMENT</label>
              <input
                type="text"
                name="department"
                value={formData.department}
                readOnly
                className="w-full border border-gray-300 rounded-md p-1 bg-gray-100"
              />

              <label>HEAD QUARTER</label>
              <select
                name="headQuarter"
                value={formData.headQuarter}
                onChange={handleChange}
                disabled={pendingApiCount > 0}
                className="w-full border border-gray-300 rounded-md p-1 disabled:opacity-50"
              >
                <option value="">
                  {pendingApiCount > 0 ? "Loading…" : "Select"}
                </option>
                {headquarters.map((hq) => (
                  <option key={hq.id} value={hq.id}>
                    {hq.name}
                  </option>
                ))}
              </select>

              <label>COUNTRY</label>
              <input
                type="text"
                name="country"
                value={formData.country}
                readOnly
                className="w-full border border-gray-300 rounded-md p-1 bg-gray-100"
              />

              <label className="editemployee-required-label">DOB</label>
              <div>
                <input
                  type="date"
                  name="dob"
                  value={formData.dob}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-1"
                />
                {errors.dob && (
                  <p className="text-red-500 text-xs mt-1">{errors.dob}</p>
                )}
              </div>

              <label>GENDER</label>
              <div className="flex gap-3">
                <label>
                  <input
                    type="radio"
                    name="gender"
                    value="Male"
                    checked={formData.gender === "Male"}
                    onChange={handleChange}
                  />{" "}
                  Male
                </label>
                <label>
                  <input
                    type="radio"
                    name="gender"
                    value="Female"
                    checked={formData.gender === "Female"}
                    onChange={handleChange}
                  />{" "}
                  Female
                </label>
              </div>
            </div>
          </fieldset>

          {/* ── Location Details ──────────────────────────────────────────── */}
          <fieldset className="editemployee-section border rounded-md p-4">
            <legend className="bg-blue-500 border rounded text-white px-3">
              Location Details
            </legend>

            <div className="editemployee-grid-container">
              <label>ADDRESS</label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md p-1"
              />

              <label>PIN</label>
              <div>
                <input
                  type="text"
                  name="pin"
                  value={formData.pin}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-1"
                />
                {errors.pin && (
                  <p className="text-red-500 text-xs mt-1">{errors.pin}</p>
                )}
              </div>

              <label>EMAIL</label>
              <div>
                <input
                  type="text"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-1"
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                )}
              </div>

              <label className="editemployee-required-label">MOBILE</label>
              <div>
                <input
                  type="text"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md p-1"
                />
                {errors.mobile && (
                  <p className="text-red-500 text-xs mt-1">{errors.mobile}</p>
                )}
              </div>

              <label>REMARKS</label>
              <textarea
                name="remarks"
                value={formData.remarks}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md p-1"
              />
            </div>
          </fieldset>

          {/* ── Employment Details ────────────────────────────────────────── */}
          <fieldset className="editemployee-section border rounded-md p-4">
            <legend className="bg-blue-500 border rounded text-white px-3">
              Employment Details
            </legend>
            <div className="editemployee-grid-container">
              <label className="editemployee-required-label">
                FINAL JOINING DATE
              </label>
              <input
                type="date"
                name="finalJoiningDate"
                value={formData.finalJoiningDate}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md p-1"
              />

              <label>STATUS</label>
              <div className="flex gap-2">
                <label>
                  <input
                    type="radio"
                    name="status"
                    value="WORKING"
                    checked={formData.status === "WORKING"}
                    onChange={handleChange}
                  />{" "}
                  WORKING
                </label>
                <label>
                  <input
                    type="radio"
                    name="status"
                    value="RESIGNED"
                    checked={formData.status === "RESIGNED"}
                    onChange={handleChange}
                  />{" "}
                  RESIGNED
                </label>
              </div>

              {formData.status === "RESIGNED" ? (
                <>
                  <label>LEFT DATE</label>
                  <input
                    type="date"
                    name="leftDate"
                    value={formData.leftDate}
                    max="2099-12-31"
                    onChange={handleChange}
                    className="w-full border rounded-md p-1"
                  />

                  <label>RESIGNATION DATE</label>
                  <input
                    type="date"
                    name="resignationDate"
                    value={formData.resignationDate}
                    onChange={handleChange}
                    className="w-full border rounded-md p-1"
                  />

                  <label>TRAINING DATE</label>
                  <input
                    type="date"
                    name="trainingDate"
                    value={formData.trainingDate}
                    max="2099-12-31"
                    onChange={handleChange}
                    className="w-full border rounded-md p-1"
                  />

                  <label>CONFIRM DATE</label>
                  <input
                    type="date"
                    name="confirmDate"
                    value={formData.confirmDate}
                    max="2099-12-31"
                    onChange={handleChange}
                    className="w-full border rounded-md p-1"
                  />

                  <label>LEFT REASON</label>
                  <input
                    type="text"
                    name="leftReason"
                    value={formData.leftReason}
                    onChange={handleChange}
                    className="w-full border rounded-md p-1"
                  />

                  <label>NOC DATE</label>
                  <input
                    type="date"
                    name="nocDate"
                    value={formData.nocDate}
                    max="2099-12-31"
                    onChange={handleChange}
                    className="w-full border rounded-md p-1"
                  />

                  <label>FULL AND FINAL DATE</label>
                  <input
                    type="date"
                    name="fullAndFinalDate"
                    value={formData.fullAndFinalDate}
                    max="2099-12-31"
                    onChange={handleChange}
                    className="w-full border rounded-md p-1"
                  />

                  <label>FINAL AMOUNT</label>
                  <input
                    type="text"
                    name="finalAmount"
                    value={formData.finalAmount}
                    onChange={handleChange}
                    className="w-full border rounded-md p-1"
                  />
                </>
              ) : null}
            </div>
          </fieldset>

          {/* ── Action buttons ────────────────────────────────────────────── */}
          <div className="editemployee-buttons flex gap-4 mt-4">
            <CustomButton
              variant="update"
              type="submit"
              disabled={isFormBusy}
              title={
                isFormBusy
                  ? "Please wait for all data to finish loading before saving"
                  : "Update employee"
              }
              style={{
                width: "90px",
                height: "40px",
                opacity: isFormBusy ? 0.5 : 1,
                cursor: isFormBusy ? "not-allowed" : "pointer",
              }}
            >
              {getButtonLabel()}
            </CustomButton>
            <CustomButton
              variant="cancel"
              onClick={() => navigate("/select-employee")}
              style={{ width: "90px", height: "40px" }}
            >
              Return
            </CustomButton>
          </div>
        </form>
      </div>

      {/* ── Update History ─────────────────────────────────────────────────── */}
      <div className="p-2 border-2 border-gray-300 rounded-lg bg-white mt-5">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
          <span className="text-blue-700">Update </span>
          <span className="text-blue-500">History</span>
        </h1>

        <div className="editemployee-history-table-wrapper">
          <table className="editemployee-history-table">
            <thead className="text-white bg-blue-500">
              <tr>
                <th>S.No</th>
                <th>Emp ID</th>
                <th>Employee</th>
                <th>Gender</th>
                <th>Father Name</th>
                <th>Manager</th>
                <th>Email</th>
                <th>City</th>
                <th>State</th>
                <th>Sub State</th>
                <th>HQ</th>
                <th>Division</th>
                <th>Desg</th>
                <th>DOB</th>
                <th>Joining Date</th>
                <th>Resign</th>
                <th>Address</th>
                <th>Pin</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Resignation Date</th>
                <th>Updated By</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {history.map((entry, index) => (
                <tr
                  key={entry.id ?? `${entry.employee_id}-${index}`}
                  className={
                    index % 2 === 0
                      ? "editemployee-even-row"
                      : "editemployee-odd-row"
                  }
                >
                  <td>{index + 1}</td>
                  <td>{entry.employee_name}</td>
                  <td>{`${entry.first_name} ${entry.last_name}`.trim()}</td>
                  <td>{entry.gender}</td>
                  <td>
                    {`${entry?.father_name != null ? entry?.father_name : "-"}`.trim()}
                  </td>
                  <td>{entry.manager || "-"}</td>
                  <td>{entry.email || "-"}</td>
                  <td>{entry.city || "-"}</td>
                  <td>{entry.state || "-"}</td>
                  <td>{entry.substate || "-"}</td>
                  <td>{entry.headquarter || "-"}</td>
                  <td>{entry.division || "-"}</td>
                  <td>{entry.designation || "-"}</td>
                  <td>{formatDate(entry.dob) || "-"}</td>
                  <td>{formatDate(entry.final_joining_date) || "-"}</td>
                  <td>{entry.status === "RESIGNED" ? "Yes" : "No"}</td>
                  <td className="tooltip">
                    <span className="tooltiptext">{entry.address || "-"}</span>
                  </td>
                  <td>{entry.pin || "-"}</td>
                  <td>{entry.mobile || "-"}</td>
                  <td>{entry.email || "-"}</td>
                  <td>{formatDate(entry.resign_date) || "-"}</td>
                  <td>{entry.updated_by || "-"}</td>
                  <td>{formatDate(entry.start_time) || "-"}</td>
                  <td>{formatDate(entry.end_time) || "-"}</td>
                  <td>{entry?.remarks || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EditEmployeeForm;